'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Linkedin } from 'lucide-react';
import Link from 'next/link';
import { useTeam } from '@/hooks/useApi';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { TeamRole } from '@/types';
import { resolveImageUrl } from '@/lib/image';
import { slugifyRouteValue } from '@/lib/slug';

export const Team: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { data, isLoading } = useTeam(1, 100);

    if (isLoading) return <Loader />;

    // Group team members by role
    const ceo = data?.items.filter(m => m.role === TeamRole.CEO) || [];
    const executives = data?.items.filter(m =>
        m.role === TeamRole.COO || m.role === TeamRole.CTO || m.role === TeamRole.CFO
    ) || [];
    const others = data?.items.filter(m =>
        m.role !== TeamRole.CEO && m.role !== TeamRole.COO && m.role !== TeamRole.CTO && m.role !== TeamRole.CFO
    ) || [];

    return (
        <div className="blog-page">
            <div className="container">
                <div className="page-header">
                    <h1>{t('team.title')}</h1>
                    <p>{t('team.subtitle')}</p>
                </div>

                {/* CEO Section */}
                {ceo.length > 0 && (
                    <div style={{ marginBottom: 'var(--spacing-16)' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--spacing-8)' }}>
                            {ceo.map((member) => (
                                <Link
                                    key={member.id}
                                    href={`/team/${slugifyRouteValue(i18n.language === 'de' && member.name_de ? member.name_de : member.name)}`}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Card className="team-card team-card-centered team-card-max">
                                    {member.image && (
                                        <img
                                            src={resolveImageUrl(member.image)}
                                            alt={member.name}
                                            loading="lazy"
                                            decoding="async"
                                            width={150}
                                            height={150}
                                            style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto var(--spacing-4)' }}
                                        />
                                    )}
                                    <h3>{i18n.language === 'de' && member.name_de ? member.name_de : member.name}</h3>
                                    <p style={{ color: 'var(--color-primary)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--spacing-3)' }}>
                                        {member.role && t(`teamRoles.${member.role}`)}
                                    </p>
                                    {member.linkedin && (
                                        <a
                                            href={member.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(event) => event.stopPropagation()}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}
                                        >
                                            <Linkedin size={20} />
                                            LinkedIn
                                        </a>
                                    )}
                                    </Card>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {/* Executives */}
                {executives.length > 0 && (
                    <div style={{ marginBottom: 'var(--spacing-12)' }}>
                        <h2 style={{ textAlign: 'center', marginBottom: 'var(--spacing-8)' }}>Executive Team</h2>
                        <div className="blog-grid">
                            {executives.map((member) => (
                                <Link
                                    key={member.id}
                                    href={`/team/${slugifyRouteValue(i18n.language === 'de' && member.name_de ? member.name_de : member.name)}`}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Card hover className="team-card team-card-centered">
                                    {member.image && (
                                        <img
                                            src={resolveImageUrl(member.image)}
                                            alt={member.name}
                                            loading="lazy"
                                            decoding="async"
                                            width={120}
                                            height={120}
                                            style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto var(--spacing-4)' }}
                                        />
                                    )}
                                    <h4>{i18n.language === 'de' && member.name_de ? member.name_de : member.name}</h4>
                                    <p style={{ color: 'var(--color-primary)', fontWeight: 'var(--font-weight-medium)', marginBottom: 'var(--spacing-3)' }}>
                                        {member.role && t(`teamRoles.${member.role}`)}
                                    </p>
                                    {member.linkedin && (
                                        <a
                                            href={member.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(event) => event.stopPropagation()}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)' }}
                                        >
                                            <Linkedin size={16} />
                                            LinkedIn
                                        </a>
                                    )}
                                    </Card>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {/* Other Team Members */}
                {others.length > 0 && (
                    <div>
                        <h2 style={{ textAlign: 'center', marginBottom: 'var(--spacing-8)' }}>Our Team</h2>
                        <div className="blog-grid">
                            {others.map((member) => (
                                <Link
                                    key={member.id}
                                    href={`/team/${slugifyRouteValue(i18n.language === 'de' && member.name_de ? member.name_de : member.name)}`}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Card hover className="team-card team-card-centered">
                                    {member.image && (
                                        <img
                                            src={resolveImageUrl(member.image)}
                                            alt={member.name}
                                            loading="lazy"
                                            decoding="async"
                                            width={100}
                                            height={100}
                                            style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto var(--spacing-3)' }}
                                        />
                                    )}
                                    <h5>{i18n.language === 'de' && member.name_de ? member.name_de : member.name}</h5>
                                    <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-2)' }}>
                                        {member.role && t(`teamRoles.${member.role}`)}
                                    </p>
                                    {member.linkedin && (
                                        <a
                                            href={member.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(event) => event.stopPropagation()}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)' }}
                                        >
                                            <Linkedin size={16} />
                                        </a>
                                    )}
                                    </Card>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
